import { setupAGGrid } from "@/lib/AGGrid/setupAGGrid";
import { ReportWorkOrdersTable } from "@/app/Reports/Reports/Components/ReportWorkOrders/ReportWorkOrdersTable";
import {
  ReportWorkOrderListItem,
  reportWorkOrderListQuery,
} from "@/app/Reports/Reports/Components/ReportWorkOrders/GraphQL/reportWorkOrdersQuery";
import { useCallback, useRef } from "react";
import { Button } from "@/components/catalyst/button";
import { AgGridReact } from "ag-grid-react";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { QuarterSelector } from "@/app/Reports/Reports/Components/QuarterSelector";

setupAGGrid();

function getQuarterDateRange(
  quarter: number,
  year: number,
): { start: string; end: string } {
  // Australian FY: Q1=Jul-Sep, Q2=Oct-Dec, Q3=Jan-Mar, Q4=Apr-Jun
  const quarterMonths: Record<number, [number, number]> = {
    1: [7, year - 1],
    2: [10, year - 1],
    3: [1, year],
    4: [4, year],
  };

  const [startMonth, calendarYear] = quarterMonths[quarter];
  const startDate = new Date(calendarYear, startMonth - 1, 1);
  const endDate = new Date(calendarYear, startMonth + 2, 0);

  const formatDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  return {
    start: formatDate(startDate),
    end: formatDate(endDate),
  };
}

function getQuarterDateRangeDisplay(quarter: number, year: number): string {
  const quarterMonths: Record<number, [number, number]> = {
    1: [7, year - 1],
    2: [10, year - 1],
    3: [1, year],
    4: [4, year],
  };

  const [startMonth, calendarYear] = quarterMonths[quarter];
  const start = new Date(calendarYear, startMonth - 1, 1);
  const end = new Date(calendarYear, startMonth + 2, 0);

  const formatDate = (date: Date) =>
    date.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  return `${formatDate(start)} – ${formatDate(end)}`;
}

export function ReportWorkOrdersScreen() {
  const gridRef = useRef<AgGridReact<ReportWorkOrderListItem>>(null);

  const { quarter, year } = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/work-orders",
  });

  const navigate = useNavigate({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/work-orders",
  });

  const dateRange = getQuarterDateRange(quarter, year);

  const { data, isLoading } = useGqlQuery(reportWorkOrderListQuery, {
    rangeStartDate: dateRange.start,
    rangeEndDate: dateRange.end,
  });

  function handleQuarterChange(newQuarter: number, newYear: number) {
    navigate({
      params: (prev) => prev,
      search: { quarter: newQuarter, year: newYear },
    });
  }

  const onBtExport = useCallback(
    (type: "csv" | "xls") => () => {
      const onlySelected = gridRef.current?.api.getSelectedRows().length !== 0;

      if (type === "csv") {
        gridRef.current?.api.exportDataAsCsv({
          onlySelected,
        });
      } else {
        gridRef.current?.api.exportDataAsExcel({
          onlySelected,
        });
      }
    },
    [],
  );

  return (
    <div className="flex h-full flex-col space-y-4 p-8">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/reports"
              params={(prev) => prev}
            >
              Reports
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Work Orders</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">
            Work Order Profit Report
          </h2>
          <p className="text-sm text-gray-500">
            {getQuarterDateRangeDisplay(quarter, year)}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <QuarterSelector
            quarter={quarter}
            year={year}
            onQuarterChange={handleQuarterChange}
          />
          <Button outline onClick={onBtExport("csv")}>
            Export CSV
          </Button>
          <Button onClick={onBtExport("xls")}>Export Excel</Button>
        </div>
      </div>

      <div className="rounded-md bg-blue-50 p-4">
        <div className="text-sm text-blue-700">
          <strong>Note:</strong> Row values are shown in each work order&apos;s
          currency. The total row at the bottom converts all values to AUD using
          fixed exchange rates.
        </div>
      </div>

      <div className="grow rounded-lg">
        <ReportWorkOrdersTable
          ref={gridRef}
          data={data?.reportWorkOrders ?? []}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
